import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../ConnectStrip';
import { FAQ } from '../FAQ';
import { Head } from '../Head';
import { Hero } from '../Hero';
import { IndustriesServed } from '../IndustriesServed';
import { Summary } from '../Summary';
import { WhySitePen } from '../WhySitePen';
import * as css from './Development.m.css';
import * as commonCss from '../../Common.m.css';
import * as qualityAssuranceCss from './QualityAssurance.m.css';

const compatibilityImg = require('../../assets/images/services/compatibility-testing.svg');
const ensureQualityImg = require('../../assets/images/development/code-reuse.svg');
const functionalityImg = require('../../assets/images/development/enterprise-software-ux-design.svg');
const performanceImg = require('../../assets/images/services/performance-testing.svg');
const qaBenefitsImg = require('../../assets/images/development/engineering.svg');
const securityImg = require('../../assets/images/services/security-testing.svg');
const technologiesImg = require('../../assets/images/services/technologies.svg');
const usabilityImg = require('../../assets/images/services/usability-testing.svg');

const factory = create();

export const QualityAssurance = factory(function QualityAssurance() {
	return (
		<div classes={css.root}>
			<Head
				title="Software Quality Assurance &amp; Testing | SitePen"
				description="SitePen employs enterprise-grade software quality assurance and testing strategies throughout the entire development cycle to ensure customer satisfaction."
			/>
			<div classes={[css.heroWrapper]}>
				<div classes={commonCss.contentWrapper}>
					<Hero>
						{{
							xlTitle: 'Software Quality Assurance',
							blurb: 'Focusing on quality in every step of the development process.'
						}}
					</Hero>
				</div>
			</div>
			<section classes={[commonCss.largePaddingTop, commonCss.contentWrapper]}>
				<Summary smaller reverse collapse="large" margin={false}>
					{{
						title: 'Ensure the Quality of Your Web Application',
						blurb: (
							<p>
								Customers want your web applications to be fast and attractive, but
								they <em classes={commonCss.emphasis}>expect</em> them to work
								flawlessly. Web applications have become the most common way that
								customers interact with you and your company. While many customers
								will tolerate some slowness in an application, they have little
								tolerance for sites that crash or lose their data. Assuring that
								your applications meet this rigorous standard demands that quality
								is considered throughout the application's development, not just as
								a final check at the end of the project. Gathering accurate
								requirements, breaking business needs into technical tasks, and
								thoroughly testing the application are all essential to ensuring the
								web application attains this goal.
							</p>
						),
						image: (
							<img
								src={ensureQualityImg}
								classes={commonCss.responsiveImage}
								alt=""
							/>
						)
					}}
				</Summary>
			</section>
			<WhySitePen image="consulting" />
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.contentWrapper}>
					<h2>Software Quality Assurance Services</h2>
					<p classes={[commonCss.largeMarginBottom, commonCss.paragraphWidth]}>
						We utilize multiple testing strategies to verify the quality of our
						solutions.
					</p>
				</div>
				<div classes={commonCss.smallContentWrapper}>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Functionality Testing',
							blurb: (
								<p>
									Functionality testing is intended to guarantee that the
									application performs as intended. It focuses on the solution's
									correctness and is typically tested against the product
									requirements identified at the beginning of the project and
									refined during its development. Functionality testing generally
									is verified through a combination of{' '}
									<Link to="servicesAutomatedTesting">automated tests</Link>,
									which codify the application's business rules, and manual
									testing by experienced quality assurance personnel.
								</p>
							),
							image: (
								<img
									src={functionalityImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Usability Testing',
							blurb: `Many projects are functionally correct but end up as failures. These failures are often caused by poorly designed user-experiences that hamper adoption. Usability testing serves to verify the assumptions made during the design of the application's interfaces and interactions (i.e., its UX) by engaging real users to test and evaluate how well the application guides them through their workflows.`,
							image: (
								<img
									src={usabilityImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Performance Testing',
							blurb: (
								<p>
									<Link to="servicesPerformanceTesting">Performance testing</Link>{' '}
									ensures that the application works quickly and efficiently to
									process user requests. The goal of this testing is not merely to
									blindly accelerate the application. Instead, performance testing
									focuses on the most common workflows, optimizing them to improve
									user efficiency and ensure that they can stay focused on the
									task at hand. Performance testing also extends beyond the single
									user to verify that the application can handle expected
									workloads without degrading the user experience and can scale as
									demands increase.
								</p>
							),
							image: (
								<img
									src={performanceImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Security Testing',
							blurb: `All applications have security concerns. While it is tempting to rely on the company firewall for internal business applications, almost every application has features that should only be accessed by a small group of qualified personnel. Security testing involves a comprehensive review of the application in its production environment. In addition to thoroughly probing the application for vulnerabilities, such testing often consists of verifying that the secondary systems, such as identity providers, are sufficiently hardened that they can't be used as an attack vector into the application.`,
							image: (
								<img
									src={securityImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller margin={false}>
						{{
							title: 'Compatibility Testing',
							blurb: `Users have an extensive set of applications and platforms that they can use to access today's web applications. Compatibility testing is the process of verifying the functionality, usability, performance, and security of an application for every option that users have. `,
							image: (
								<img
									src={compatibilityImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<div classes={[commonCss.section, commonCss.contentWrapper]}>
				<Summary
					reverse
					trailingBias="medium"
					blurbGutter="medium"
					imageGutter="medium"
					margin={false}
				>
					{{
						title: 'Technologies We Use',
						blurb: (
							<virtual>
								<p>
									SitePen has worked with all of the major quality control tools
									that support the development of today's web applications. Our
									engineering teams have developed deep expertise in the available
									technologies. They can readily assemble a suite of testing tools
									that support each project's quality requirements without
									overloading it with extraneous elements.
								</p>
								<ul classes={[commonCss.list, qualityAssuranceCss.twoColumnList]}>
									<li>Selenium</li>
									<li>Postman</li>
									<li>Chai</li>
									<li>Jasmine</li>
									<li>Cypress</li>
									<li>Mocha</li>
									<li>Karma</li>
									<li>Sinon</li>
								</ul>
							</virtual>
						),
						image: (
							<img
								src={technologiesImg}
								classes={commonCss.responsiveImage}
								loading="lazy"
								alt=""
							/>
						)
					}}
				</Summary>
			</div>
			<section classes={commonCss.shaded}>
				<IndustriesServed />
			</section>
			<section classes={[commonCss.section, commonCss.smallContentWrapper]}>
				<img
					src={qaBenefitsImg}
					classes={commonCss.centeredHeaderImage}
					alt=""
					loading="lazy"
				/>
				<h2>Benefits of Enterprise Quality Assurance</h2>
				<p>
					One of the harsh realities of software development is that{' '}
					<em classes={commonCss.emphasis}>someone</em> will find flaws in a software
					system. The goal and benefit of enterprise quality assurance are to ensure that
					the defects are found by the development and maintenance teams, not the
					customer. Each flaw or bug that makes it to the end-user erodes their confidence
					in the entire enterprise software ecosystem.
				</p>
				<h2 classes={commonCss.mediumPaddingTop}>
					Choosing the Right Software Quality Assurance Company
				</h2>
				<p>
					The companies that provide the best software quality assurance services consider
					quality from the beginning of the project through to final delivery and launch.
					Best-in-class quality assurance processes look to identify each project's
					critical elements that serve as the key quality-drivers during the sales and
					requirements gathering phases. During development, engineering teams should
					craft the software using robust code organization patterns and a comprehensive
					set of automated tests to ensure that quality targets are achieved. As the
					project nears its conclusion, the application should be exposed to a battery of
					software quality control tools that confirm that the product is functional,
					performant, secure, and easy to use.
				</p>
			</section>
			<ConnectStrip />
			<FAQ title="Quality Assurance FAQs">
				{[
					{
						title:
							'What Is the Difference Between Quality Control and Quality Assurance?',
						content: [
							<p>
								Quality control encompasses tools and practices that are focused on
								identifying errors that exist in a system. This is a vital practice
								for any engineering project to adopt, but it is typically
								insufficient for many enterprise applications.
							</p>,
							<p>
								Quality assurance is a broader concept that takes advantage of any
								tool or practice to prevent defects from making it to the customer.
								Quality assurance processes integrate throughout the entire
								development project, including requirements gathering, development,
								and final quality checks.
							</p>
						]
					},
					{
						title: 'What Is the Importance of Software Quality Assurance?',
						content: (
							<p>
								Software quality is directly tied to user confidence, so defects
								that consumers discover will decrease their satisfaction with, and
								willingness to use, the defective software. If the users are outside
								of the enterprise, customers will likely seek alternative solutions
								to defective software, often allowing competitors to gain market
								share. Within the enterprise, faulty software can trigger the
								creation of unofficial systems that attempt to meet the business
								need in a better way. These{' '}
								<em classes={commonCss.emphasis}>shadow</em> systems are often
								poorly developed and maintained leading to quality, maintenance,
								reporting, and scaling issues.
							</p>
						)
					},
					{
						title: 'What Are the Steps in Quality Assurance?',
						content: (
							<virtual>
								<p>
									The steps involved in quality assurance are easy to identify -
									they are precisely the same steps as the product development
									process. QA is meant to consider quality throughout the project;
									it is not intended as another set of procedures that must be
									followed. There are four primary phases to a development project
									and, by extension, the QA process.
								</p>
								<ul classes={commonCss.list}>
									<li>Requirements gathering</li>
									<ul classes={[commonCss.list, commonCss.listCircle]}>
										<li>
											QA during the requirements gathering phases seeks to
											capture the key indicators that will drive the project's
											quality. Additionally, the requirements are conveyed to
											the testing team to start developing the manual and
											automated testing strategies that will verify that each
											requirement is met.
										</li>
									</ul>
									<li>Development</li>
									<ul classes={[commonCss.list, commonCss.listCircle]}>
										<li>
											During the development phase, the QA process tasks
											engineering teams with building quality into the code
											that they are writing. Engineering teams will often use
											code reviews, automated tests, and code refactoring to
											continually refine the codebase.
										</li>
									</ul>
									<li>Testing and verification</li>
									<ul classes={[commonCss.list, commonCss.listCircle]}>
										<li>
											As the project nears its conclusion, the testing team
											will apply quality control tools to identify any defects
											that the product contains. The development team will
											analyze and resolve each issue.
										</li>
									</ul>
									<li>Delivery</li>
									<ul classes={[commonCss.list, commonCss.listCircle]}>
										<li>
											The final phase in the QA process is delivery. During
											this phase, end-users have the opportunity to exercise
											their standard workflows in an isolated environment. Any
											defects found in this phase are then corrected by the
											engineering team and serve as a final check to ensure
											that the solution is as error free as possible.
										</li>
									</ul>
								</ul>
							</virtual>
						)
					},
					{
						title: 'What Are the Best Practices for Software Quality Assurance?',
						content: (
							<virtual>
								<p>
									While the QA process's tools and specific practices vary widely,
									based on individual project requirements, some traits are
									universally present.
								</p>
								<p>
									Quality assurance is most effective when multi-disciplinary
									teams are involved. When the team comprises project managers,
									developers, testers, business analysts, and actual users, the
									team can achieve a deeper understanding of what quality means
									for the project and how to achieve it.
								</p>
								<p>
									Understanding which tools and practices are being used is also
									vital to any QA process. There are far too many tools available
									for any QA process to adopt them all. Instead, a subset of the
									available tools must be selected to form a lean but robust net
									that balances resource requirements with the ability to identify
									and eliminate defects.
								</p>
							</virtual>
						)
					}
				]}
			</FAQ>
		</div>
	);
});

export default QualityAssurance;
